<!-- EditModule.vue -->
<template>
  <BaseModal
    :is-visible="isVisible"
    title="Edit Module"
    subtitle="Updating module to the system"
    :icon="Edit"
    size="2xl"
    :show-footer="true"
    @close="closeModal"
  >
    <div class="space-y-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Module Name -->
        <div class="form-group">
          <label
            for="module-name"
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            Module Name <span class="text-red-500">*</span>
          </label>
          <input
            id="module-name"
            v-model="formData.name"
            type="text"
            class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            :class="{
              'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.name
            }"
            placeholder="Enter module name"
            required
            @input="checkForChanges"
          />
          <p v-if="errors.name" class="mt-2 text-sm text-red-600 font-medium">
            {{ errors.name }}
          </p>
        </div>

        <!-- Module Description -->
        <div class="form-group">
          <label
            for="module-description"
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            Description
          </label>
          <textarea
            id="module-description"
            v-model="formData.description"
            rows="4"
            class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
            :class="{
              'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                errors.description
            }"
            placeholder="Enter module description (optional)"
            @input="checkForChanges"
          ></textarea>
          <p v-if="errors.description" class="mt-2 text-sm text-red-600 font-medium">
            {{ errors.description }}
          </p>
        </div>

        <!-- Current Status Display (Read-only) -->
        <div class="form-group">
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-600 gap-3"
          >
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Module Status
            </label>
            <span
              class="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold shadow-sm"
              :class="statusClass"
            >
              {{ statusText }}
            </span>
          </div>
        </div>

        <!-- Current File Display -->
        <div v-if="currentFileUrl" class="form-group">
          <label
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            Current File
          </label>
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div class="flex items-center min-w-0 flex-1">
              <FileText class="h-8 w-8 text-blue-500 mr-3 flex-shrink-0" />
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ currentFileName }}
                </p>
                <p class="text-xs text-gray-500">
                  {{ fileType?.toUpperCase() || 'Unknown type' }}
                </p>
              </div>
            </div>
            <button
              v-if="isAdmin"
              type="button"
              @click="downloadFile"
              :disabled="isDownloading"
              class="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200 ml-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              title="Download file (Admin only)"
            >
              <Loader2 v-if="isDownloading" class="h-5 w-5 animate-spin" />
              <Download v-else class="h-5 w-5" />
            </button>
            <div
              v-else-if="currentFileUrl"
              class="text-gray-400 dark:text-gray-500 ml-2 flex-shrink-0"
              title="Download restricted to administrators"
            >
              <Download class="h-5 w-5" />
            </div>
          </div>
          
          <!-- Admin-only download notice for non-admin users -->
          <div v-if="currentFileUrl && !isAdmin" class="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center">
            <Info class="w-4 h-4 mr-2" />
            Download restricted to administrators
          </div>
        </div>

        <!-- File Upload Section -->
        <div class="form-group">
          <label
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            {{ currentFileUrl ? 'Replace File' : 'Module File' }}
          </label>
          <div class="space-y-4">
            <!-- File Upload Area -->
            <div
              class="relative border-2 border-dashed rounded-xl p-6 transition-colors duration-200 cursor-pointer"
              :class="[
                isDragOver
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
              ]"
              @drop="onFileDrop"
              @dragover.prevent="onDragOver"
              @dragenter.prevent="onDragEnter"
              @dragleave.prevent="onDragLeave"
              @click="triggerFileInput"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".pdf,.doc,.docx"
                class="hidden"
                @change="onFileSelect"
              />

              <div v-if="!selectedFile" class="text-center">
                <template v-if="isDragOver">
                  <Upload class="mx-auto h-12 w-12 text-blue-500 mb-4" />
                  <div class="text-sm font-medium text-blue-600 dark:text-blue-400">
                    Release to drop your file
                  </div>
                  <p class="text-xs text-blue-500/80 dark:text-blue-400/80 mt-1">
                    PDF, DOC, DOCX up to 10MB
                  </p>
                </template>
                <template v-else>
                  <Upload
                    class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4"
                  />
                  <div class="text-sm text-gray-600 dark:text-gray-300">
                    <span
                      class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
                    >
                      Click to upload
                    </span>
                    or drag and drop
                  </div>
                  <p class="text-xs text-gray-500 mt-2">PDF, DOC, DOCX up to 10MB</p>
                </template>
              </div>

              <!-- Selected File Display -->
              <div
                v-else
                class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div class="flex items-center min-w-0 flex-1">
                  <FileText class="h-8 w-8 text-blue-500 mr-3 flex-shrink-0" />
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-gray-900 truncate">
                      {{ selectedFile.name }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ formatFileSize(selectedFile.size) }}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200 flex-shrink-0"
                  @click.stop="removeFile"
                  title="Remove file"
                >
                  <X class="h-5 w-5" />
                </button>
              </div>
            </div>
            <p v-if="errors.file" class="text-sm text-red-600 font-medium">
              {{ errors.file }}
            </p>
          </div>
        </div>

        <!-- Confirmation for replacing file -->
        <div
          v-if="needsReplaceConfirmation"
          class="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4"
        >
          <div class="font-semibold text-yellow-800 mb-2">Confirmation Required</div>
          <div class="text-sm text-yellow-700 mb-2">
            Type <span class="font-bold">REPLACE</span> to confirm replacing the current file:
          </div>
          <input
            v-model="replaceConfirmation"
            type="text"
            class="w-full px-3 py-2 border border-yellow-300 rounded text-sm"
            placeholder="Type REPLACE to confirm"
            autocomplete="off"
          />
        </div>

        <!-- Live validation messages -->
        <div class="mt-3 space-y-2">
          <div
            v-if="nameValidationMessage"
            class="flex items-center text-sm text-red-600 dark:text-red-400 font-medium"
          >
            <XCircle class="w-4 h-4 mr-2 flex-shrink-0" />
            {{ nameValidationMessage }}
          </div>
          <div
            v-else-if="isFormValid && hasChanges && needsReplaceConfirmation && !isReplaceConfirmed"
            class="flex items-center text-sm text-yellow-700 dark:text-yellow-400 font-medium"
          >
            <Info class="w-4 h-4 mr-2 flex-shrink-0" />
            Please confirm module file replacement to proceed
          </div>
          <div
            v-else-if="isFormValid && !nameValidationMessage && hasChanges && isReplaceConfirmed"
            class="flex items-center text-sm text-green-600 dark:text-green-400 font-medium"
          >
            <CheckCircle class="w-4 h-4 mr-2 flex-shrink-0" />
            Ready to update module
          </div>
          <div
            v-else-if="isFormValid && !nameValidationMessage && !hasChanges"
            class="flex items-center text-sm text-gray-600 dark:text-gray-400 font-medium"
          >
            <Info class="w-4 h-4 mr-2 flex-shrink-0" />
            Make changes to enable update
          </div>
        </div>
      </form>
    </div>

    <template #footer>
      <BaseButton
        text="Cancel"
        variant="secondary"
        :disabled="isLoading"
        @click="closeModal"
      />
      <BaseButton
        :text="isLoading ? 'Updating...' : 'Update Module'"
        variant="primary"
        :loading="isLoading"
        :disabled="isLoading || !canUpdate"
        @click="handleSubmit"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick } from 'vue'
import {
  Edit,
  Upload,
  FileText,
  Download,
  X,
  Loader2,
  XCircle,
  CheckCircle,
  Info
} from 'lucide-vue-next'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { ModulesService, type Module } from '@/services/modules'
import { useAuthStore } from '@/stores/auth'
import { useToastService } from '@/services/toast'
import { getErrorMessage } from '@/utils/errorHandler'

interface Props {
  isVisible: boolean
  module?: Module | null
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  module: null
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'success', module: Module & { hasFileUpdate?: boolean }): void
}>()

// Reactive data
const formData = reactive({
  name: '',
  description: '',
  file: null as File | null
})

const errors = reactive({
  name: '',
  description: '',
  file: ''
})

const isLoading = ref(false)
const isDragOver = ref(false)
const selectedFile = ref<File | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const hasChanges = ref(false)
const isDownloading = ref(false)
const downloadId = ref<string | null>(null)
const initialData = reactive({
  name: '',
  description: ''
})

// Auth and toast composables
const authStore = useAuthStore()
const toast = useToastService()

// Computed properties
const moduleId = computed(() => props.module?.module_id)
const currentFileUrl = computed(() => props.module?.material_filepath)
const currentFileName = computed(() => {
  if (!currentFileUrl.value) return ''
  const url = currentFileUrl.value
  return url.substring(url.lastIndexOf('/') + 1)
})
const fileType = computed(() => props.module?.file_type || '')
const statusText = computed(() => (props.module?.status === 1 ? 'Active' : 'Inactive'))
const statusClass = computed(() =>
  props.module?.status === 1
    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
)
const isFormValid = computed(() => formData.name.trim().length > 0)
const nameValidationMessage = computed(() => {
  if (!formData.name.trim()) return 'Module name is required'
  if (formData.name.trim().length < 3) return 'Module name must be at least 3 characters'
  return ''
})
const isAdmin = computed(() => authStore.isAdmin)

const replaceConfirmation = ref('')
// New computed properties for replace confirmation
const needsReplaceConfirmation = computed(() =>
  !!currentFileUrl.value && !!selectedFile.value
)

const isReplaceConfirmed = computed(() =>
  !needsReplaceConfirmation.value || replaceConfirmation.value.trim().toUpperCase() === 'REPLACE'
)

// Update isFormValid to include replace confirmation
const canUpdate = computed(() =>
  isFormValid.value && hasChanges.value && isReplaceConfirmed.value
)

// Methods
const closeModal = () => {
  // Cancel any pending downloads
  if (downloadId.value) {
    downloadId.value = null
  }
  
  // Reset download state
  isDownloading.value = false
  
  emit('close')
}

const checkForChanges = () => {
  // Check if name or description has changed
  const nameChanged = formData.name !== initialData.name
  const descriptionChanged = formData.description !== initialData.description
  const fileChanged = selectedFile.value !== null

  hasChanges.value = nameChanged || descriptionChanged || fileChanged
}

const triggerFileInput = () => {
  fileInput.value?.click()
}

const onFileSelect = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files?.length) {
    const file = input.files[0]
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    if (!allowedTypes.includes(file.type)) {
      errors.file = 'Please select a PDF, DOC, or DOCX file'
      // Reset the input
      if (fileInput.value) {
        fileInput.value.value = ''
      }
      return
    }
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      errors.file = 'File size must be less than 10MB'
      // Reset the input
      if (fileInput.value) {
        fileInput.value.value = ''
      }
      return
    }
    
    // Clear any previous file errors
    errors.file = ''
    
    // If there is a current file, show confirmation input instead of window.confirm
    if (currentFileUrl.value) {
      selectedFile.value = file
      replaceConfirmation.value = ''
      checkForChanges()
      return
    }
    selectedFile.value = file
    checkForChanges()
  }
}

const onFileDrop = async (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = false

  if (event.dataTransfer?.files.length) {
    const file = event.dataTransfer.files[0]
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    if (!allowedTypes.includes(file.type)) {
      errors.file = 'Please select a PDF, DOC, or DOCX file'
      return
    }
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      errors.file = 'File size must be less than 10MB'
      return
    }
    
    // Clear any previous file errors
    errors.file = ''
    
    // If there is a current file, show confirmation input instead of window.confirm
    if (currentFileUrl.value) {
      selectedFile.value = file
      replaceConfirmation.value = ''
      checkForChanges()
      return
    }
    selectedFile.value = file
    checkForChanges()
  }
}

const removeFile = () => {
  selectedFile.value = null
  replaceConfirmation.value = ''
  errors.file = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  checkForChanges()
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const handleSubmit = async () => {
  if (!isFormValid.value || !moduleId.value || !hasChanges.value) return

  isLoading.value = true
  try {
    // Update the module details
    const updatePayload = {
      name: formData.name,
      description: formData.description
    }

    const updateResponse = await ModulesService.updateModule(moduleId.value, updatePayload)

    // If there's a new file to upload, handle it
    if (selectedFile.value) {
      await ModulesService.uploadModuleFile(moduleId.value, selectedFile.value)
    }

    // Emit success with module ID so parent can refresh the specific module
    emit('success', { 
      ...updateResponse.data, 
      hasFileUpdate: !!selectedFile.value 
    })
    closeModal()
  } catch (error: any) {
    console.error('Error updating module:', error)
    errors.name = error.message || 'Failed to update module. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// Drag and drop handlers
const onDragOver = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = true
}

const onDragEnter = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = true
}

const onDragLeave = () => {
  isDragOver.value = false
}

// Download file function
const downloadFile = async () => {
  if (!currentFileUrl.value || !moduleId.value) {
    toast.error('No file available', 'This module does not have a file attached.')
    return
  }

  if (!isAdmin.value) {
    toast.error('Access Denied', 'Only administrators can download module materials.')
    return
  }

  // Prevent multiple simultaneous downloads
  if (isDownloading.value) {
    toast.warning('Download in Progress', 'Please wait for the current download to complete.')
    return
  }

  isDownloading.value = true
  
  // Generate unique download ID for this session
  const currentDownloadId = `download_${moduleId.value}_${Date.now()}`
  downloadId.value = currentDownloadId
  
  try {
    const blob = await ModulesService.downloadFile(moduleId.value)
    
    // Check if this download is still current (user hasn't cancelled)
    if (downloadId.value !== currentDownloadId) {
      return
    }
    
    // Create download link with cache-busting
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // Generate filename with timestamp to avoid cache conflicts
    const originalFileName = currentFileName.value || `module_${moduleId.value}.pdf`
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
    const fileExtension = originalFileName.split('.').pop()
    const baseName = originalFileName.replace(/\.[^/.]+$/, '')
    const fileName = `${baseName}_${timestamp}.${fileExtension}`
    
    link.download = fileName
    
    // Add cache-busting attributes
    link.setAttribute('download', fileName)
    link.style.display = 'none'
    
    // Trigger download
    document.body.appendChild(link)
    link.click()
    
    // Cleanup with slight delay to ensure download starts
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link)
      }
      window.URL.revokeObjectURL(url)
    }, 100)
    
    toast.success('Download Started', `Downloading ${originalFileName}`)
  } catch (error: any) {
    console.error('Download failed:', error)
    toast.error('Download Failed', getErrorMessage(error, 'Failed to download the file.'))
  } finally {
    isDownloading.value = false
    downloadId.value = null
  }
}

// Reset form function
const resetForm = () => {
  formData.name = ''
  formData.description = ''
  formData.file = null
  selectedFile.value = null
  isDragOver.value = false
  hasChanges.value = false
  errors.name = ''
  errors.description = ''
  errors.file = ''

  // Clear file input
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Initialize form data
const initializeForm = () => {
  if (props.module) {
    formData.name = props.module.name || ''
    formData.description = props.module.description || ''

    // Store initial values for change detection
    initialData.name = props.module.name || ''
    initialData.description = props.module.description || ''

    // Reset changes flag
    hasChanges.value = false
    selectedFile.value = null
  }
}

// Reset form when modal closes or initialize when it opens
watch(
  () => props.isVisible,
  isVisible => {
    if (!isVisible) {
      // Reset form data when modal closes
      resetForm()
    } else if (isVisible && props.module) {
      // Initialize form when modal becomes visible
      nextTick(() => {
        initializeForm()
      })
    }
  }
)

// Initialize form when module prop changes
watch(
  () => props.module,
  () => {
    if (props.isVisible && props.module) {
      // Use nextTick to ensure DOM is updated
      nextTick(() => {
        initializeForm()
      })
    }
  },
  { immediate: true }
)
</script>

<style scoped>
/* Custom styles for file upload area */
.form-group {
  margin-bottom: 0.25rem;
}
</style>
